import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getCheckoutResult } from "@/lib/checkout/checkout.functions";
import { useCart } from "@/lib/cart/cart-store";
import { trackPurchase } from "@/lib/analytics/tracker";
import { CheckCircle2, Package, Truck, Mail, Loader2, MapPin, Phone, User } from "lucide-react";

export const Route = createFileRoute("/checkout_/success")({
  head: () => ({
    meta: [
      { title: "Thank you for your order — FIXHEN" },
      { name: "description", content: "Your FIXHEN order is confirmed. We'll email tracking as soon as it ships." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (
    search: Record<string, unknown>,
  ): { session_id?: string; payment_intent?: string; redirect_status?: string } => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
    // Express (Apple Pay / Google Pay) checkout returns a PaymentIntent
    // instead of a Checkout Session.
    payment_intent: typeof search.payment_intent === "string" ? search.payment_intent : undefined,
    redirect_status:
      typeof search.redirect_status === "string" ? search.redirect_status : undefined,
  }),
  component: SuccessPage,
});

function money(n?: number | string | null, currency = "EUR") {
  const v = typeof n === "number" ? n : Number(n ?? 0);
  return new Intl.NumberFormat("en-IE", { style: "currency", currency }).format(Number.isFinite(v) ? v : 0);
}

function SuccessPage() {
  const { session_id, payment_intent } = Route.useSearch();
  const clear = useCart((s) => s.clear);
  const ref = session_id ?? payment_intent;

  const { data, isLoading } = useQuery({
    queryKey: ["checkout-result", ref],
    queryFn: () =>
      getCheckoutResult({
        data: session_id ? { sessionId: session_id } : { paymentIntentId: payment_intent! },
      }),
    enabled: !!ref,
    refetchInterval: (q) => {
      const d = q.state.data as any;
      if (!d || !d.found) return 1500;
      if (d.order?.status === "pending_payment") {
        // Stop polling after ~30 seconds so users don't spin forever on a
        // truly failed payment.
        const attempts = (q.state.dataUpdateCount ?? 0);
        if (attempts >= 15) return false;
        return 2000;
      }
      return false;
    },
  });

  // Clear the cart as soon as we've confirmed the order exists in the DB for
  // this session_id — regardless of whether Stripe has posted the webhook yet.
  // Otherwise the shopper sees their old cart and thinks nothing was ordered.
  useEffect(() => {
    if (data && (data as any).found) clear();
  }, [data, clear]);

  // Report the conversion once the order is actually confirmed as paid.
  // Guarded per-order inside trackPurchase, so refreshes/polling never
  // double-count revenue.
  useEffect(() => {
    const o = data && (data as any).found ? ((data as any).order as any) : null;
    if (!o || o.status === "pending_payment") return;
    trackPurchase({
      orderId: o.id,
      orderNumber: o.order_number,
      totalEur: Number(o.total_eur ?? 0),
      items: ((data as any).items ?? []).length,
      method: payment_intent ? "express" : "standard",
    });
  }, [data, payment_intent]);

  const order = data?.found ? (data.order as any) : null;
  const items = data?.found ? (data.items ?? []) : [];
  const isConfirmed = order && order.status !== "pending_payment";
  const shippingLines = order
    ? [order.shipping_line1, order.shipping_line2, [order.shipping_postal_code, order.shipping_city].filter(Boolean).join(" "), order.country]
        .filter((x) => x && String(x).trim().length)
    : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/60 via-white to-white">
      <AnnouncementBar />
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-4 py-10 md:py-14">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-emerald-200 bg-white shadow-sm">
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-emerald-100/80 to-transparent" />
          <div className="relative px-6 py-10 text-center md:px-10 md:py-12">
            <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
              <CheckCircle2 className="h-11 w-11" strokeWidth={2.5} />
            </div>
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-emerald-900 md:text-4xl">
              Thank you for your order!
            </h1>
            {order ? (
              <>
                <p className="mt-2 text-sm text-emerald-800/80 md:text-base">
                  Order <span className="font-mono font-bold text-emerald-900">#{order.order_number}</span>
                  {order.customer_email ? (
                    <> — a confirmation has been sent to <b>{order.customer_email}</b></>
                  ) : null}
                </p>
                <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
                  {isConfirmed ? (
                    <>
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      Payment received
                    </>
                  ) : (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Confirming payment…
                    </>
                  )}
                </div>
                {(order as any).invoice_requested ? (
                  <div className="mx-auto mt-5 max-w-md rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-left text-sm text-sky-900">
                    <p className="font-bold">🧾 Your invoice is on its way</p>
                    <p className="mt-0.5 text-xs text-sky-800">
                      We'll email the invoice{(order as any).billing_snapshot?.legal_name ? <> for <b>{(order as any).billing_snapshot.legal_name}</b></> : null} to{" "}
                      <b>{order.customer_email || "your email"}</b> within 1 business day.
                      {(order as any).billing_scheme === "eu_b2b_reverse_charge" ? " Reverse charge applied — no VAT was charged." : ""}
                    </p>
                  </div>
                ) : null}
              </>
            ) : isLoading || !ref ? (
              <p className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-800/80">
                <Loader2 className="h-4 w-4 animate-spin" /> Finalising your order…
              </p>
            ) : (
              <p className="mt-2 text-sm text-emerald-800/80">
                Your payment is being confirmed. You'll receive an email shortly.
              </p>
            )}
          </div>
        </div>

        {/* Order + delivery details */}
        {order && (
          <div className="mt-6 grid gap-6 md:grid-cols-5">
            <section className="md:col-span-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Order summary</h2>
              <ul className="mt-4 divide-y divide-slate-100">
                {items.map((it: any, i: number) => (
                  <li key={i} className="flex items-center justify-between gap-3 py-3 text-sm">
                    <div className="min-w-0">
                      <p className="line-clamp-2 font-semibold text-slate-900">{it.product_name}</p>
                      <p className="mt-0.5 text-[11px] text-slate-500 font-mono">
                        {it.product_sku} · Qty {it.quantity}
                      </p>
                    </div>
                    <span className="shrink-0 tabular-nums font-semibold text-slate-900">
                      {money(it.line_total_eur, order.currency)}
                    </span>
                  </li>
                ))}
              </ul>
              <dl className="mt-4 space-y-1.5 border-t border-slate-100 pt-4 text-sm">
                <div className="flex justify-between text-slate-600">
                  <dt>Subtotal</dt>
                  <dd className="tabular-nums">{money(order.subtotal_eur, order.currency)}</dd>
                </div>
                <div className="flex justify-between text-slate-600">
                  <dt>Shipping</dt>
                  <dd className="tabular-nums">{money(order.shipping_eur, order.currency)}</dd>
                </div>
                {Number(order.tax_eur) > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <dt>Tax</dt>
                    <dd className="tabular-nums">{money(order.tax_eur, order.currency)}</dd>
                  </div>
                )}
                <div className="flex items-baseline justify-between border-t border-slate-100 pt-3 text-base">
                  <dt className="font-bold text-slate-900">Total</dt>
                  <dd className="font-extrabold text-emerald-700 tabular-nums text-lg">
                    {money(order.total_eur, order.currency)}
                  </dd>
                </div>
              </dl>
            </section>

            <aside className="md:col-span-2 space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500">
                  <MapPin className="h-4 w-4 text-emerald-600" /> Shipping to
                </h2>
                <div className="mt-3 space-y-1 text-sm">
                  {order.customer_name && (
                    <p className="flex items-center gap-1.5 font-semibold text-slate-900">
                      <User className="h-3.5 w-3.5 text-slate-400" /> {order.customer_name}
                    </p>
                  )}
                  {shippingLines.map((l: string, i: number) => (
                    <p key={i} className="text-slate-700">{l}</p>
                  ))}
                  {order.phone && (
                    <p className="mt-2 flex items-center gap-1.5 text-slate-600">
                      <Phone className="h-3.5 w-3.5 text-slate-400" /> {order.phone}
                    </p>
                  )}
                </div>
              </div>

              {order.tracking_number && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
                  <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-emerald-800">
                    <Truck className="h-4 w-4" /> On its way
                  </h2>
                  <p className="mt-2 text-sm text-emerald-900">
                    {order.tracking_carrier}
                  </p>
                  <p className="mt-1 font-mono text-sm font-semibold text-emerald-900">
                    {order.tracking_number}
                  </p>
                </div>
              )}
            </aside>
          </div>
        )}

        {/* Next steps */}
        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          <StepCard icon={Mail} title="Confirmation email" text="Check your inbox for the receipt and next steps." />
          <StepCard icon={Package} title="Preparing your parts" text="We inspect and pack every order by hand within 1–2 business days." />
          <StepCard icon={Truck} title="Tracking on the way" text="You'll get a tracking number the moment we ship your parcel." />
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/shop"
            className="inline-flex h-11 items-center rounded-md bg-emerald-600 px-6 font-semibold text-white shadow-sm hover:bg-emerald-700"
          >
            Continue shopping
          </Link>
          <Link
            to={"/account/orders" as any}
            className="inline-flex h-11 items-center rounded-md border border-slate-300 bg-white px-6 font-semibold text-slate-800 hover:bg-slate-50"
          >
            View my orders
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function StepCard({
  icon: Icon,
  title,
  text,
}: {
  icon: typeof Mail;
  title: string;
  text: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-600 shadow-sm">
      <Icon className="mb-1.5 h-5 w-5 text-emerald-600" />
      <b className="block text-sm text-slate-900">{title}</b>
      <p className="mt-0.5">{text}</p>
    </div>
  );
}
